import React, { useState } from 'react';
import { Trash2, AlertTriangle, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const DriverDeleteAccountModal = ({ onClose, onDeleted, pendingEarnings }) => {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm.');
      return;
    }
    setDeleting(true);
    setError('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase?.from('driver_profiles')?.update({ status: 'inactive' })?.eq('user_id', user?.id);
        await supabase?.from('user_profiles')?.update({ is_active: false })?.eq('id', user?.id);
        await supabase?.auth?.signOut();
        onDeleted?.();
      }
    } catch (err) {
      setError(err?.message || 'Account deletion failed. Please contact support.');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Delete Driver Account</h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">
          {step === 1 ? (
            <>
              {pendingEarnings > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <p className="text-sm font-semibold text-amber-800">⚠️ Pending Earnings: KSh {pendingEarnings?.toLocaleString()}</p>
                  <p className="text-xs text-amber-700 mt-1">You have unpaid earnings. These will be processed before account deletion.</p>
                </div>
              )}
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-red-700 font-medium mb-2">This will permanently delete your driver account.</p>
                <ul className="text-sm text-red-600 space-y-1 list-disc list-inside">
                  <li>All trip history and ratings will be removed</li>
                  <li>Documents and verifications will be deleted</li>
                  <li>Referral earnings will be forfeited</li>
                  <li>You cannot re-register with the same details</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <button onClick={onClose} className="btn-secondary flex-1">
                  Keep Account
                </button>
                <button onClick={() => setStep(2)} className="btn-danger flex-1">
                  Continue
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600 mb-4">
                Type <span className="font-bold text-red-600">DELETE</span> to permanently delete your account:
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e?.target?.value)}
                placeholder="Type DELETE to confirm"
                className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 mb-3"
                autoFocus
              />
              {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
              <div className="flex gap-3">
                <button onClick={onClose} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting || confirmText !== 'DELETE'}
                  className="btn-danger flex-1"
                >
                  {deleting ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const DriverDangerZone = ({ onDeleted, pendingEarnings }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
        <h3 className="font-semibold text-red-700 mb-1 flex items-center gap-2">
          <Trash2 className="w-4 h-4" />
          Danger Zone
        </h3>
        <p className="text-sm text-red-600 mb-4">Deleting your driver account is permanent. Any pending earnings will be processed first.</p>
        <button
          onClick={() => setShowModal(true)}
          className="btn-danger flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Delete Driver Account
        </button>
      </div>
      {showModal && (
        <DriverDeleteAccountModal
          onClose={() => setShowModal(false)}
          onDeleted={onDeleted}
          pendingEarnings={pendingEarnings}
        />
      )}
    </>
  );
};

export default DriverDangerZone;
